import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { copyTextToClipboard, shareURL } from '@tma.js/sdk-react';
import { Check, Copy, Send, Swords } from 'lucide-react';

import { env } from '@/env';
import type { InviteController } from '@/hooks/useInvite';

interface InviteSheetProps {
  invite: InviteController;
  /** Close the sheet (also cancels a pending invite via the parent). */
  onClose: () => void;
}

/** m:ss countdown to the invite's `expiresAt`. */
function formatRemaining(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

/**
 * Creator-side challenge sheet. Drives length pick → "create" → the big code
 * with Share/Copy → the waiting state (countdown + cancel). Mirrors the
 * RankedQueueSheet shell (dim backdrop + rounded bottom sheet, brand accents).
 *
 * Share: no generic start-param routing exists in this TMA (only `auth_<code>`
 * is dispatched at boot, by useLogtoAuth — there is no app-boot router for
 * arbitrary deep links), so per the brief we ship SHARE-AS-TEXT: `shareURL`
 * opens Telegram's native contact picker with a message that carries the code
 * + instructions to paste it into "Ho un codice invito". The acceptor enters
 * the code manually — no app-boot plumbing is invented.
 */
export function InviteSheet({ invite, onClose }: InviteSheetProps) {
  const { t } = useTranslation('backgammon');
  const { status, pending, accepted, error, createInvite, cancelInvite } = invite;

  const [matchLength, setMatchLength] = useState<1 | 3 | 5>(3);
  const [copied, setCopied] = useState(false);
  const [remaining, setRemaining] = useState(0);

  // Countdown to expiry while an invite is pending (or just shown).
  useEffect(() => {
    if (!pending) {
      setRemaining(0);
      return;
    }
    const target = Date.parse(pending.expiresAt);
    const tick = () => setRemaining(target - Date.now());
    tick();
    const id = window.setInterval(tick, 1_000);
    return () => window.clearInterval(id);
  }, [pending]);

  // Reset the "copied" flash whenever the code changes.
  useEffect(() => {
    setCopied(false);
  }, [pending?.code]);

  function close() {
    if (pending && status !== 'accepted') cancelInvite(pending.code);
    onClose();
  }

  function shareToTelegram() {
    if (!pending) return;
    // The bot's Main Mini App link; the message body carries the code so the
    // recipient can paste it into "Ho un codice invito" inside the app.
    const appLink = `https://t.me/${env.telegramBotUsername}`;
    const text = t('invite.shareText', { code: pending.code, app: appLink });
    // shareURL opens Telegram's native contact share sheet with a prefilled
    // message. Falls back to a no-op outside Telegram (dev browser).
    try {
      shareURL(appLink, text);
    } catch {
      /* not in Telegram — Copy remains available */
    }
  }

  async function copyCode() {
    if (!pending) return;
    try {
      await copyTextToClipboard(pending.code);
    } catch {
      try {
        await navigator.clipboard.writeText(pending.code);
      } catch {
        /* clipboard unavailable */
      }
    }
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1_500);
  }

  // While `accepted` the parent navigates immediately; render a tiny confirm
  // frame so the sheet doesn't flash empty during the transition.
  const showAccepted = status === 'accepted' && accepted;

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center"
      role="dialog"
      aria-modal="true"
    >
      <button
        type="button"
        aria-label={t('invite.close')}
        onClick={close}
        className="absolute inset-0 bg-black/60"
      />
      <div className="relative w-full max-w-md rounded-t-2xl bg-bg-secondary p-4 pb-[max(1rem,env(safe-area-inset-bottom))]">
        <div className="mb-3 flex items-center gap-2">
          <h2 className="flex items-center gap-2 text-base font-bold text-fg">
            <Swords className="h-4 w-4 text-brand" />
            {t('invite.title')}
          </h2>
        </div>

        {showAccepted ? (
          /* ── accepted: brief confirmation before the parent navigates ───── */
          <div className="flex flex-col items-center gap-4 py-6">
            <span className="flex h-12 w-12 items-center justify-center rounded-full bg-brand/15 text-brand">
              <Check className="h-6 w-6" />
            </span>
            <p className="text-center text-base font-bold text-fg">
              {t('invite.acceptedBy', { name: accepted.opponent.username })}
            </p>
          </div>
        ) : pending ? (
          /* ── pending: the code + share/copy + countdown + cancel ────────── */
          <div className="flex flex-col items-center gap-4 py-2">
            <p className="text-xs font-medium text-fg-muted">{t('invite.codeLabel')}</p>
            <div className="select-all rounded-xl border border-border bg-bg px-4 py-3 text-center text-2xl font-bold tracking-[0.25em] text-fg tabular-nums">
              {pending.code}
            </div>

            <div className="flex w-full gap-2">
              <button
                type="button"
                onClick={shareToTelegram}
                className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-brand py-3 text-sm font-bold text-brand-fg transition-opacity active:opacity-80"
              >
                <Send className="h-4 w-4" />
                {t('invite.share')}
              </button>
              <button
                type="button"
                onClick={copyCode}
                className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-bg-tertiary py-3 text-sm font-bold text-fg transition-opacity active:opacity-80"
              >
                {copied ? <Check className="h-4 w-4 text-brand" /> : <Copy className="h-4 w-4" />}
                {copied ? t('invite.copied') : t('invite.copy')}
              </button>
            </div>

            <p className="text-xs tabular-nums text-fg-muted">
              {t('invite.waiting')} ({t('invite.expires', { time: formatRemaining(remaining) })})
            </p>

            <button
              type="button"
              onClick={close}
              className="w-full rounded-xl bg-bg-tertiary py-3 text-sm font-bold text-fg-muted transition-opacity active:opacity-80"
            >
              {t('invite.cancel')}
            </button>
          </div>
        ) : (
          /* ── idle: pick length → create ─────────────────────────────────── */
          <div className="flex flex-col gap-4 py-2">
            <div className="space-y-1.5">
              <p className="text-xs font-medium text-fg-muted">{t('home.matchLength')}</p>
              <div className="flex gap-2">
                {([1, 3, 5] as const).map((n) => (
                  <button
                    key={n}
                    type="button"
                    onClick={() => setMatchLength(n)}
                    className={`flex-1 rounded-xl border py-2 text-sm font-semibold transition-colors ${
                      matchLength === n
                        ? 'border-brand bg-brand/15 text-brand'
                        : 'border-border bg-bg-tertiary text-fg-muted'
                    }`}
                  >
                    {t(`home.pt${n}`)}
                  </button>
                ))}
              </div>
            </div>

            {error && (
              <p className="text-sm text-danger">
                {error === 'limit_reached' ? t('invite.limit') : t('invite.createError')}
              </p>
            )}

            <button
              type="button"
              onClick={() => createInvite(matchLength)}
              disabled={status === 'creating'}
              className="w-full rounded-xl bg-brand py-3 text-sm font-bold text-brand-fg transition-opacity active:opacity-80 disabled:opacity-60"
            >
              {status === 'creating' ? t('invite.creating') : t('invite.create')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
